import React, { useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Textinput, Modal, Figure, SimpleButton, CourseImageWrapper } from '../../../ui';
import ImageUploader from '../../media/uploaders/ImageUploader';
import { useCreateCourse } from '../hooks';
import { useFeatures } from '../../settings/hooks';
import { startGuide } from '../../courseGuide/courseGuideState';

const CreateCourseModel = ({ open, onClose, onSuccess }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const createCourse = useCreateCourse();
  const features = useFeatures();

  const [courseInfo, setCourseInfo] = useState(null);
  const [courseName, setCourseName] = useState('');

  const isDisabled = !courseName?.trim();

  const closeModal = () => {
    setCourseInfo(null);
    setCourseName('');
    onClose();
  };

  const changeCourseName = (e) => {
    setCourseName(e.target.value);
  };

  const saveImage = useCallback(image => {
    if (image) setCourseInfo(prev => ({ ...prev, coverImage: image }));
  }, []);

  const handleCreateCourse = async () => {
    try {
      const data = await createCourse({ name: courseName, coverImage: courseInfo?.coverImage });
      if (onSuccess && data) {
        onSuccess(data);
      }
    } finally {
      closeModal();
    }
  };

  const handleCreateWithGuide = async () => {
    try {
      const data = await createCourse({ name: courseName, coverImage: courseInfo?.coverImage });
      if (data?.id) {
        dispatch(startGuide({ courseName, coverImage: courseInfo?.coverImage, startStep: 1 }));
        navigate(`/edit/course/${data.id}`);
      }
    } finally {
      closeModal();
    }
  };

  const imageUrl = !!courseInfo?.coverImage && (courseInfo?.coverImage?.url || courseInfo?.coverImage[0]?.url);

  const showGuideOption = !!features?.courseGuide;

  return (
    <Modal
      open={open}
      showActions={!showGuideOption}
      acceptIsDisabled={isDisabled}
      onAccept={handleCreateCourse}
      acceptLabel={t('globals.save')}
      onClose={closeModal}
      title={t('course.createNewCourse')}
      width='650px'
      dialogActionsChildren={showGuideOption ? (
        <>
          <SimpleButton variant='ghost' onClick={closeModal}>
            {t('globals.cancel')}
          </SimpleButton>
          <SimpleButton
            variant='ghost'
            onClick={handleCreateCourse}
            disabled={isDisabled}
          >
            {t('courseGuide.continueFromScratch')}
          </SimpleButton>
          <SimpleButton
            variant='primary'
            onClick={handleCreateWithGuide}
            disabled={isDisabled}
            startIcon='Edit'
          >
            {t('courseGuide.continueWithGuide')}
          </SimpleButton>
        </>
      ) : undefined}
    >
      <CourseImageWrapper>
        <ImageUploader
          type='single'
          id='courseEdit'
          saveAction={saveImage}
          imageToPreview={imageUrl}
          label={t('webshop.addCoverImage')}
          iconName='Add'
        >
          {imageUrl && <Figure.Rectangle><img alt={t('webshop.addCoverImage')} src={imageUrl} loading='lazy' /></Figure.Rectangle>}
        </ImageUploader>
      </CourseImageWrapper>
      <Textinput
        label={t('course.courseName')}
        propName='name'
        onChange={changeCourseName}
        value={courseName}
      />
    </Modal>
  );
};

export default CreateCourseModel;
